import React from 'react';

type LoaderLayoutProps = {
  /** Icon element (e.g., AiStarLoader, warning icon) */
  icon?: React.ReactNode;
  /** Content to display to the right of the icon (e.g., Checklist) */
  rightContent?: React.ReactNode;
  /** Translated title string */
  title?: string;
  /** Translated description string */
  description?: string;
};

/**
 * A generic layout component for displaying loading/processing states.
 *
 * Structure:
 * ```
 * ┌─────────────────────────────────────┐
 * │     [icon]     [rightContent]       │  ← Top row (optional)
 * │           [ title ]                 │  ← Title (optional)
 * │        [ description ]              │  ← Description (optional)
 * └─────────────────────────────────────┘
 * ```
 *
 * @example
 * // Loading state with icon
 * <LoaderLayout
 *   icon={<AiStarLoader scale={0.75} showShine />}
 *   title="Analyzing your recording"
 *   description="We're preparing your content options."
 * />
 *
 * @example
 * // Error/ineligible state without icon
 * <LoaderLayout
 *   title="This recording isn't eligible"
 *   description="This recording cannot be repurposed."
 * />
 *
 * @example
 * // With checklist on the right
 * <LoaderLayout
 *   icon={<AiStarLoader />}
 *   rightContent={<Checklist items={[...]} />}
 * />
 */
export const LoaderLayout: React.FC<LoaderLayoutProps> = ({
  icon,
  rightContent,
  title,
  description,
}) => {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center">
      {/* Top row: Icon and/or Right Content */}
      <div className="flex items-center justify-center gap-2">
        {icon}
        {rightContent && <div className="min-w-[200px]">{rightContent}</div>}
      </div>

      {/* Bottom row: Title and Description */}
      <div className="mt-4 min-h-[53px] flex flex-col items-center justify-start gap-1">
        {title && (
          <p
            key={title}
            className="m-0 text-base text-spring-neutral-b1 text-center animate-fade-in"
          >
            {title}
          </p>
        )}
        {description && (
          <p
            key={description}
            className="m-0 text-sm leading-5 text-spring-neutral-b3 text-center animate-fade-in"
          >
            {description}
          </p>
        )}
      </div>
    </div>
  );
};
